import type React from 'react'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import * as HoverCard from '@radix-ui/react-hover-card'
import dayjs from 'dayjs'

import { type TeamStatus } from '@/model/firestore/schema/TeamStatusZod'
import { type Assignment } from '@/state/assignments/types'

interface AssignmentSummaryHoverCardProps {
  assignment: Assignment
  side?: 'top' | 'bottom' | 'left' | 'right'
}

type TeamStatusGroup = 'available' | 'assigned' | 'inReturn' | 'onBreak' | 'dissolved'

// Type-safe mapping ensures all TeamStatus values are covered
const STATUS_TO_GROUP = {
  AVAILABLE_IN_LOCATION: 'available',
  AVAILABLE_IN_INSTITUTION: 'available',
  AVAILABLE_IN_REGION: 'available',
  AVAILABLE_AT_COORDINATES: 'available',
  ASSIGNED: 'assigned',
  IN_RETURN: 'inReturn',
  ON_BREAK: 'onBreak',
  DISSOLVED: 'dissolved',
} as const satisfies Record<TeamStatus, TeamStatusGroup>

export const AssignmentSummaryHoverCard: React.FC<React.PropsWithChildren<AssignmentSummaryHoverCardProps>> = ({
  assignment,
  side = 'left',
  children,
}) => {
  const intl = useIntl()

  const teamsByStatus = useMemo(() => {
    const groups: Record<TeamStatusGroup, number> = {
      available: 0,
      assigned: 0,
      inReturn: 0,
      onBreak: 0,
      dissolved: 0,
    }

    for (const team of assignment.teams) {
      const group = STATUS_TO_GROUP[team.status]
      if (group) {
        groups[group]++
      }
    }

    return groups
  }, [assignment.teams])

  const totalTeams = assignment.teams.length
  const pendingTransportRequests = assignment.transportRequests.length
  const planTime = dayjs(assignment.timestamp).format('HH:mm')

  return (
    <HoverCard.Root openDelay={200} closeDelay={100}>
      <HoverCard.Trigger asChild>{children}</HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          className="data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade data-[side=right]:animate-slideLeftAndFade data-[side=top]:animate-slideDownAndFade z-50 w-48 rounded-md border border-gray-200 bg-white p-3 shadow-[0_10px_38px_-10px_rgba(22,23,24,0.35),0_10px_20px_-15px_rgba(22,23,24,0.2)] data-[state=open]:transition-all"
          side={side}
          sideOffset={8}>
          {/* Header */}
          <div className="mb-2 border-b border-gray-100 pb-2">
            <p className="text-xs font-medium text-gray-900">
              <FormattedMessage id="assignmentSummary.planAt" values={{ time: planTime }} />
            </p>
          </div>

          {/* Teams Section */}
          <div className="mb-2">
            <div className="flex justify-between text-xs">
              <span className="font-medium text-gray-700">
                <FormattedMessage id="assignmentSummary.teams" />
              </span>
              <span className="font-medium text-gray-900">{totalTeams}</span>
            </div>
            <div className="mt-1 space-y-0.5 pl-2">
              {teamsByStatus.available > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{intl.formatMessage({ id: 'assignmentSummary.available' })}</span>
                  <span>{teamsByStatus.available}</span>
                </div>
              )}
              {teamsByStatus.assigned > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{intl.formatMessage({ id: 'assignmentSummary.assigned' })}</span>
                  <span>{teamsByStatus.assigned}</span>
                </div>
              )}
              {teamsByStatus.inReturn > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{intl.formatMessage({ id: 'assignmentSummary.inReturn' })}</span>
                  <span>{teamsByStatus.inReturn}</span>
                </div>
              )}
              {teamsByStatus.onBreak > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{intl.formatMessage({ id: 'assignmentSummary.onBreak' })}</span>
                  <span>{teamsByStatus.onBreak}</span>
                </div>
              )}
              {teamsByStatus.dissolved > 0 && (
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{intl.formatMessage({ id: 'assignmentSummary.dissolved' })}</span>
                  <span>{teamsByStatus.dissolved}</span>
                </div>
              )}
            </div>
          </div>

          {/* Transport Requests Section */}
          <div className="border-t border-gray-100 pt-2">
            <div className="flex justify-between text-xs">
              <span className="font-medium text-gray-700">
                <FormattedMessage id="assignmentSummary.pendingTR" />
              </span>
              <span className="font-medium text-gray-900">{pendingTransportRequests}</span>
            </div>
          </div>

          <HoverCard.Arrow className="fill-white" />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  )
}
